/**
 * @file editable-orders-table.tsx
 * @module app/modules/orders-delivery/components/delivery-plan/editable-orders-table
 * @description Selectable orders table used inside the edit-route-order modal;
 * supports select-all/individual checkboxes and shows moved-to-pending status.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.1
 * @version 2.1.2
 */

import { Card } from 'react-bootstrap';
import Method from '../../../../../utils/methods';
import {
  Manager,
  RouteOrderMovedToPendingOrder,
  ServiceMember,
} from '../../../../../utils/constants';
import { getOrderTypeLabel, kgToTons } from '../../utils/delivery-plan-details-helpers';
import { getRemainingOrderQuantity } from '../../utils/quantity';
import { getRemainingOrderWeight } from '../../utils/weight';

interface EditableOrdersTableProps {
  orders: any[];
  filteredOrders: any[];
  selectedId: any[];
  totalRecords: number;
  selectLoading: boolean;
  currentUser: any;
  onSelectAll: () => void;
  onChecked: (id: string) => void;
}

const EditableOrdersTable = ({
  orders,
  filteredOrders,
  selectedId,
  totalRecords,
  selectLoading,
  currentUser,
  onSelectAll,
  onChecked,
}: EditableOrdersTableProps) => {
  return (
    <Card className="border border-r10px">
      <Card.Body className="p-0">
        <div className="table-responsive">
          <div className="mh-400px scroll-y me-n7 pe-7">
            <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
              <thead>
                <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                  <th className="col-w-20" tabIndex={0} rowSpan={1} colSpan={1}>
                    <div className="form-check form-check-sm form-check-custom form-check-solid">
                      <input
                        className="form-check-input h-30px w-30px"
                        type="checkbox"
                        value="1"
                        checked={selectLoading || selectedId.length >= totalRecords}
                        onChange={() => {
                          onSelectAll();
                        }}
                        disabled={orders.length !== filteredOrders.length}
                      />
                      <label className="form-check-label fs-16 fw-600 text-dark">
                        Select all
                      </label>
                    </div>
                  </th>

                  <th className="col-w-20">Customer name</th>
                  <th className="min-w-125px">Order Type</th>
                  {currentUser?.role.length ? (
                    currentUser?.role.some(
                      (obj: any) =>
                        obj.type !== Manager || obj.type !== ServiceMember,
                    ) ? (
                      <></>
                    ) : (
                      <th className="col-w-10">Ordered by</th>
                    )
                  ) : (
                    <></>
                  )}
                  <th className="col-w-10">Quantity</th>
                  <th className="col-w-10">Weight</th>
                  <th className="col-w-10">Total amount</th>
                  <th className="col-w-20">Moved To Pending</th>
                </tr>
              </thead>
              <tbody>
                {selectLoading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3 fs-20 text-dark fw-600">
                        Please wait while processing...
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {filteredOrders.map((orderVal: any, goodIndex: number) => (
                      <tr key={goodIndex}>
                        <td>
                          <div className="form-check form-check-sm form-check-custom form-check-solid">
                            <input
                              className="form-check-input h-30px w-30px"
                              type="checkbox"
                              value={orderVal._id}
                              checked={selectedId.includes(orderVal._id)}
                              onChange={() => {
                                onChecked(orderVal._id);
                              }}
                            />
                            <div className="d-flex align-items-start flex-column ms-2">
                              <div className="d-flex flex-row">
                                <span className={'text-dark d-block fs-15 fw-500'}>
                                  {Method.convertDateToDDMMYYYYHOURS(
                                    orderVal._createdAt,
                                  )}
                                </span>
                              </div>
                              <span className={'text-gray d-block'}>
                                {orderVal.refKey}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="fs-15 fw-500 d-block">
                            {orderVal.customer.name}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500 d-block">
                            {getOrderTypeLabel(orderVal)}
                          </span>
                        </td>
                        {currentUser?.role.length ? (
                          currentUser?.role.some(
                            (obj: any) =>
                              obj.type !== Manager || obj.type !== ServiceMember,
                          ) ? (
                            <></>
                          ) : (
                            <td>{/* Placeholder for ordered by section */}</td>
                          )
                        ) : (
                          <></>
                        )}
                        <td>
                          <span className="fs-15 fw-500">
                            {`${getRemainingOrderQuantity(orderVal) ?? '-'} items`}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            {(() => {
                              const weight = getRemainingOrderWeight(orderVal);
                              return `${weight === null ? '-' : kgToTons(weight)} tons`;
                            })()}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            {'TSh ' +
                              Method.getGeneralizedAmount(
                                orderVal.payment.totalCharge,
                              )}
                          </span>
                        </td>
                        <td>
                          {orderVal.latestRouteVehicle?.status ===
                          RouteOrderMovedToPendingOrder ? (
                            <span className="badge badge-danger fs-14 fw-600 text-light p-3 border-r4px me-5">
                              Yes -{' '}
                              {Method.convertDateToDDMMYYYYHOURS(
                                orderVal.latestRouteVehicle.statusUpdatedAt,
                              )}
                            </span>
                          ) : (
                            <span className={`badge badge-light fs-14 fw-600 text-dark p-3 border-r4px me-5`}>
                              No
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EditableOrdersTable;
